import React, { useEffect } from 'react';
import ReactDOM from "react-dom";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from 'react-query'
import { useSelector, useDispatch } from 'react-redux';
import { Provider } from "react-redux";
import { applyMiddleware, compose, createStore } from "redux";
import thunk from "redux-thunk";
import promiseMiddleware from "redux-promise-middleware";
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import { createLogger } from "redux-logger";
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import { SnackbarProvider } from "notistack";
import moment from "moment";

import reducers from "./redux";
import storage from 'redux-persist/lib/storage'; // defaults to localStorage for web and AsyncStorage for react-native
import { PersistGate } from 'redux-persist/integration/react';

import bugsnag from '@bugsnag/js';
import bugsnagReact from '@bugsnag/plugin-react';
// Styles
// Import Font Awesome Icons Set
import "font-awesome/css/font-awesome.min.css";
// Import Simple Line Icons Set
import "simple-line-icons/css/simple-line-icons.css";
// Import Main styles for this application
import "./scss/style.scss";
// Containers
import Full from "./containers/Full/";
import LoginContainer from "./containers/Login/LoginContainer";
// Views
import 'react-dates/initialize';
import { login } from "./redux/users/userActions";
import { Button } from "reactstrap";

const bugsnagClient = bugsnag('48e4905a37b4b3ff86234a6923b48dec');
bugsnagClient.use(bugsnagReact, React);
const ErrorBoundary = bugsnagClient.getPlugin('react');
let middleware = [promiseMiddleware(), thunk];

console.log(process.env.NODE_ENV);

if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
	// Dev-only middleware
	console.log("DEV_only middleware");
	middleware = [
		...middleware,
		createLogger() // Logs state changes to the dev console,
	];
}

const persistConfig = {
	key: 'root',
	storage,
	whitelist: ['session', 'users'],
	transforms: [
		// nested blacklist-paths require a custom transform to be applied
		createTransform((inboundState, key) => {
			return inboundState;
		}, null),
	],
};

const persistedReducer = persistReducer(persistConfig, reducers);

const createStoreWithMiddleware = compose(composeWithDevTools(applyMiddleware(...middleware)))(createStore);
let store = createStoreWithMiddleware(persistedReducer);
let persistor = persistStore(store);

function App() {

	moment.locale('it', {
		months: 'Gennaio_Febbraio_Marzo_Aprile_Maggio_Giugno_Luglio_Agosto_Settembre_Ottobre_Novembre_Dicembre'.split('_'),
		monthsShort: 'Gen._Feb._Mar._Apr._Mag._Giu._Lug._Ago_Set._Ott._Nov._Dic.'.split('_'),
		monthsParseExact: true,
		weekdays: 'Domenica_Lunedì_Martedì_Mercoledì_Giovedì_Venerdì_Sabato'.split('_'),
		weekdaysShort: 'dim._lun._mar._mer._gio._ven._sam.'.split('_'),
		weekdaysMin: 'Do_Lu_Ma_Me_Gi_Ve_Sa'.split('_'),
		longDateFormat: {
			LT: 'HH:mm',
			LTS: 'HH:mm:ss',
			L: 'DD/MM/YYYY',
			LL: 'D MMMM YYYY',
			LLL: 'D MMMM YYYY HH:mm',
			LLLL: 'dddd D MMMM YYYY HH:mm'
		},
		week: {
			dow: 1, // Monday is the first day of the week.
		},
		relativeTime: {
			future: '%s',
			past: '%s fa',
			s: 'secondi',
			m: 'un minuto',
			mm: '%d minuti',
			h: 'un\'ora',
			hh: '%d ore',
			d: 'un giorno',
			dd: '%d giorni',
			M: 'un mese',
			MM: '%d mesi',
			y: 'un anno',
			yy: '%d anni'
		},
	});

	const user = useSelector(state => state.users);
	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(login());
	}, [dispatch]);

	if (!user.ready) return <div />;

	return (
		<BrowserRouter>
			<Routes>
				{/* public routes*/}
				<Route path="/login" element={
					<LoginContainer
						onSubmit={(username, password) => dispatch(login(username, password))}
						error={user.error}
					/>
				} />
				{/* private routes */}
				<Route element={<PrivateRoute />} >
					<Route path="/*" element={<Full />} />
				</Route>
			</Routes>
		</BrowserRouter>
	);
}

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute() {
	const user = useSelector(state => state.users);
	const location = useLocation();

	return (
		user.isLogged ? (
			<Outlet />
		) : (
			<Navigate
				to="/login"
				state={{ from: location }}
				replace
			/>
		)
	);
}

const notistackRef = React.createRef();
const onClickDismiss = key => () => {
	notistackRef.current.closeSnackbar(key);
};
const queryClient = new QueryClient()

ReactDOM.render(
	<React.StrictMode>
	<ErrorBoundary>
		<Provider store={store}>
			<PersistGate loading={null} persistor={persistor}>
				<SnackbarProvider
					ref={notistackRef}
					action={(key) => (
						<Button onClick={onClickDismiss(key)}>
							'Close'
						</Button>
					)}
					autoHideDuration={3000}>
					<QueryClientProvider client={queryClient}>
						<App />
					</QueryClientProvider>
				</SnackbarProvider>
			</PersistGate>
		</Provider>
	</ErrorBoundary>
	</React.StrictMode>,
	document.getElementById("root")
);
